import React, { useState, useEffect, useCallback } from 'react';
import {
  Phone, Mail, MapPin, StickyNote, CheckSquare, RotateCcw,
  Plus, Loader2, Trash2, Circle, CheckCircle2, AlertCircle,
  ChevronDown, ChevronUp, Calendar,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { activityService } from '../services/activityService';

// ── Config ────────────────────────────────────────────────────────────

const KIND_CONFIG = {
  note:      { label: 'Note',       icon: StickyNote,   color: 'text-slate-500',  bg: 'bg-slate-100',   dot: 'bg-slate-400'   },
  call:      { label: 'Call',       icon: Phone,        color: 'text-blue-600',   bg: 'bg-blue-100',    dot: 'bg-blue-500'    },
  email:     { label: 'Email',      icon: Mail,         color: 'text-violet-600', bg: 'bg-violet-100',  dot: 'bg-violet-500'  },
  visit:     { label: 'Visit',      icon: MapPin,       color: 'text-emerald-600',bg: 'bg-emerald-100', dot: 'bg-emerald-500' },
  task:      { label: 'Task',       icon: CheckSquare,  color: 'text-amber-600',  bg: 'bg-amber-100',   dot: 'bg-amber-500'   },
  follow_up: { label: 'Follow-up',  icon: RotateCcw,    color: 'text-orange-600', bg: 'bg-orange-100',  dot: 'bg-orange-500'  },
};

const PRIORITY_COLORS = {
  High:   'text-red-600 bg-red-50 border-red-200',
  Normal: 'text-gray-600 bg-gray-50 border-gray-200',
  Low:    'text-green-600 bg-green-50 border-green-200',
};

const isTask = (kind) => kind === 'task' || kind === 'follow_up';

const isOverdue = (activity) =>
  isTask(activity.kind) &&
  activity.status === 'open' &&
  activity.dueDate &&
  new Date(activity.dueDate) < new Date();

const formatDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  const now  = new Date();
  const diff = (date - now) / (1000 * 60 * 60 * 24);
  if (diff < -1) return `Overdue: ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  if (diff < 0)  return 'Due yesterday';
  if (diff < 1)  return 'Due today';
  if (diff < 2)  return 'Due tomorrow';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatOccurred = (d) =>
  new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

// ── Add Activity Form ─────────────────────────────────────────────────

// Default to today's date and current time for the log form
const todayDate = () => new Date().toISOString().slice(0, 10);
const nowTime   = () => new Date().toTimeString().slice(0, 5); // "HH:MM"

const EMPTY_FORM = {
  kind: 'note', title: '', description: '',
  dueDate: '', priority: 'Normal', outcome: '',
  occurredDate: todayDate(), occurredTime: nowTime(),
};

const AddActivityForm = ({ tenantId, propertyId, onSaved, onCancel }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        kind:        form.kind,
        title:       form.title.trim(),
        description: form.description.trim(),
        priority:    form.priority,
      };
      if (tenantId)  payload.tenant   = tenantId;
      if (propertyId) payload.property = propertyId;
      if (isTask(form.kind) && form.dueDate) {
        payload.dueDate = form.dueDate;
      }
      if (!isTask(form.kind)) {
        // Combine date + time into ISO string; fall back to now if date is blank
        if (form.occurredDate) {
          const time = form.occurredTime || '00:00';
          payload.occurredAt = new Date(`${form.occurredDate}T${time}`).toISOString();
        }
        if (form.outcome) payload.outcome = form.outcome.trim();
      }

      const saved = await activityService.create(payload);
      setForm(EMPTY_FORM);
      onSaved(saved);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
      <p className="text-sm font-semibold text-gray-700">Log activity or add task</p>
      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* Kind selector */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(KIND_CONFIG).map(([k, cfg]) => {
          const Icon = cfg.icon;
          return (
            <button
              key={k}
              type="button"
              onClick={() => set('kind', k)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                form.kind === k
                  ? `${cfg.bg} ${cfg.color} border-current`
                  : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
              }`}
            >
              <Icon className="h-3 w-3" />
              {cfg.label}
            </button>
          );
        })}
      </div>

      {/* Title */}
      <Input
        placeholder={isTask(form.kind) ? 'What needs to be done?' : 'What happened?'}
        value={form.title}
        onChange={e => set('title', e.target.value)}
        className="text-sm"
        disabled={saving}
      />

      {/* Task: due date + priority / Log: occurred + outcome */}
      <div className="grid grid-cols-2 gap-2">
        {isTask(form.kind) ? (
          <>
            <div>
              <Label className="text-xs text-gray-500 mb-1">Due date</Label>
              <Input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} className="text-sm h-8" disabled={saving} />
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1">Priority</Label>
              <select
                value={form.priority}
                onChange={e => set('priority', e.target.value)}
                className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                disabled={saving}
              >
                <option value="High">High</option>
                <option value="Normal">Normal</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </>
        ) : (
          <>
            <div className="col-span-2">
              <Label className="text-xs text-gray-500 mb-1">When</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={form.occurredDate}
                  onChange={e => set('occurredDate', e.target.value)}
                  className="text-sm h-9"
                  disabled={saving}
                />
                <Input
                  type="time"
                  value={form.occurredTime}
                  onChange={e => set('occurredTime', e.target.value)}
                  className="text-sm h-9"
                  disabled={saving}
                />
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-gray-500 mb-1">Outcome</Label>
              <Input placeholder="e.g., Left voicemail" value={form.outcome} onChange={e => set('outcome', e.target.value)} className="text-sm h-9" disabled={saving} />
            </div>
          </>
        )}
      </div>

      {/* Notes */}
      <textarea
        placeholder="Additional notes (optional)"
        value={form.description}
        onChange={e => set('description', e.target.value)}
        rows={2}
        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        disabled={saving}
      />

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
};

// ── Activity Item ─────────────────────────────────────────────────────

const ActivityItem = ({ activity, onComplete, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg     = KIND_CONFIG[activity.kind] || KIND_CONFIG.note;
  const Icon    = cfg.icon;
  const overdue = isOverdue(activity);
  const done    = activity.status === 'completed';

  return (
    <div className={`flex gap-3 group ${done ? 'opacity-60' : ''}`}>
      {/* Timeline dot + line */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`h-7 w-7 rounded-full ${done ? 'bg-gray-100' : cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <Icon className={`h-3.5 w-3.5 ${done ? 'text-gray-400' : cfg.color}`} />
        </div>
        <div className="w-px flex-1 bg-gray-100 mt-1" />
      </div>

      {/* Content */}
      <div className="flex-1 pb-4 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Task checkbox */}
              {isTask(activity.kind) && (
                <button
                  onClick={() => onComplete(activity)}
                  className="flex-shrink-0 text-gray-400 hover:text-emerald-600 transition-colors"
                  title={done ? 'Reopen' : 'Mark complete'}
                >
                  {done
                    ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    : <Circle className="h-4 w-4" />
                  }
                </button>
              )}
              <span className={`text-sm font-medium ${done ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                {activity.title}
              </span>
              {activity.priority && activity.priority !== 'Normal' && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${PRIORITY_COLORS[activity.priority]}`}>
                  {activity.priority}
                </span>
              )}
            </div>

            {/* Date line */}
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {isTask(activity.kind) && activity.dueDate && (
                <span className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                  {overdue && <AlertCircle className="h-3 w-3" />}
                  <Calendar className="h-3 w-3" />
                  {formatDate(activity.dueDate)}
                </span>
              )}
              {!isTask(activity.kind) && (
                <span className="text-xs text-muted-foreground">
                  {formatOccurred(activity.occurredAt || activity.createdAt)}
                </span>
              )}
              {activity.tenant && (
                <span className="text-xs text-muted-foreground">
                  · {activity.tenant.name}
                </span>
              )}
            </div>

            {/* Expandable detail */}
            {(activity.description || activity.outcome) && (
              <button
                onClick={() => setExpanded(v => !v)}
                className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-gray-700 mt-1 transition-colors"
              >
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {expanded ? 'Hide' : 'Show notes'}
              </button>
            )}
            {expanded && (
              <div className="mt-2 text-xs text-gray-600 bg-white border border-gray-100 rounded-lg px-3 py-2 space-y-1">
                {activity.description && <p>{activity.description}</p>}
                {activity.outcome     && <p className="text-muted-foreground italic">{activity.outcome}</p>}
              </div>
            )}
          </div>

          {/* Delete */}
          <button
            onClick={() => onDelete(activity._id)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-300 hover:text-red-500 flex-shrink-0 mt-0.5"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── ActivityFeed ──────────────────────────────────────────────────────

const FILTER_OPTIONS = [
  { key: 'all',   label: 'All' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'logs',  label: 'Calls & Logs' },
];

const ActivityFeed = ({ tenantId, propertyId, title = 'Activity' }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState('all');
  const [showForm, setShowForm]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tenantId)   params.tenantId   = tenantId;
      if (propertyId) params.propertyId = propertyId;
      const data = await activityService.getAll(params);
      setActivities(data);
    } catch (err) {
      console.error('Failed to load activities:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantId, propertyId]);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (saved) => {
    setActivities(prev => [saved, ...prev]);
    setShowForm(false);
  };

  const handleComplete = async (activity) => {
    try {
      const updated = activity.status === 'completed'
        ? await activityService.reopen(activity._id)
        : await activityService.complete(activity._id);
      setActivities(prev => prev.map(a => a._id === updated._id ? updated : a));
    } catch (err) {
      console.error('Failed to toggle activity:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await activityService.delete(id);
      setActivities(prev => prev.filter(a => a._id !== id));
    } catch (err) {
      console.error('Failed to delete activity:', err);
    }
  };

  const filtered = activities.filter(a => {
    if (filter === 'tasks') return isTask(a.kind);
    if (filter === 'logs')  return !isTask(a.kind);
    return true;
  });

  const openTasks = activities.filter(a => isTask(a.kind) && a.status === 'open');
  const overdueTasks = openTasks.filter(isOverdue);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          {openTasks.length > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {openTasks.length} open task{openTasks.length !== 1 ? 's' : ''}
              {overdueTasks.length > 0 && (
                <span className="text-red-600 font-medium"> · {overdueTasks.length} overdue</span>
              )}
            </p>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowForm(v => !v)} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          Add
        </Button>
      </div>

      {/* Add form */}
      {showForm && (
        <AddActivityForm
          tenantId={tenantId}
          propertyId={propertyId}
          onSaved={handleSaved}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Filter tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {FILTER_OPTIONS.map(opt => (
          <button
            key={opt.key}
            onClick={() => setFilter(opt.key)}
            className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
              filter === opt.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          {filter === 'all'
            ? 'No activity yet. Log a call, note, or add a task.'
            : filter === 'tasks'
              ? 'No tasks yet.'
              : 'No calls or logs yet.'
          }
        </div>
      ) : (
        <div className="mt-2">
          {filtered.map(activity => (
            <ActivityItem
              key={activity._id}
              activity={activity}
              onComplete={handleComplete}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
